import type { ReactNode } from 'react';
import { TrendingUp, Activity, DollarSign, Globe, Target, CreditCard } from 'lucide-react';
import type { IndustryBenchmarkRecord, RegionConfig } from '../../types/industryResearch';
import { formatCurrencyValue, formatPercent } from '../financial/utils/formatters';

interface Props {
  summary: IndustryBenchmarkRecord;
  region?: RegionConfig;
  companiesCount: number;
}

interface CardProps {
  label: string;
  value: string;
  sub?: string;
  iconBg: string;
  iconColor: string;
  icon: ReactNode;
}

function MetricCard({ label, value, sub, iconBg, iconColor, icon }: CardProps) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs text-neutral-500 font-medium uppercase tracking-wide leading-tight">
          {label}
        </p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ml-2 ${iconBg}`}>
          <span className={iconColor}>{icon}</span>
        </div>
      </div>
      <p className="text-xl font-bold text-neutral-900 leading-tight">{value}</p>
      {sub && <p className="text-xs text-neutral-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function IndustryBenchmarkMetricCards({ summary, region, companiesCount }: Props) {
  const unit = region?.currency?.unit_abbr ?? '';
  const basis = `Median · ${companiesCount} co${companiesCount !== 1 ? 's' : ''}`;

  const roicAboveWacc =
    summary.roic != null && summary.wacc != null && summary.roic > summary.wacc;

  const ebitdaMarginSub =
    summary.ebitda_margin != null
      ? `Margin: ${formatPercent(summary.ebitda_margin)}`
      : basis;

  const evSub =
    summary.ev_ebitda != null
      ? `EV/EBITDA: ${summary.ev_ebitda.toFixed(1)}x`
      : basis;

  const roicSub =
    summary.wacc != null
      ? `WACC: ${formatPercent(summary.wacc)}`
      : basis;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <MetricCard
        label="Median Revenue"
        value={formatCurrencyValue(summary.total_revenue, region)}
        sub={`${basis} · ${unit}`}
        iconBg="bg-blue-50"
        iconColor="text-blue-600"
        icon={<TrendingUp className="h-4 w-4" />}
      />
      <MetricCard
        label="Median EBITDA"
        value={formatCurrencyValue(summary.ebitda, region)}
        sub={ebitdaMarginSub}
        iconBg="bg-indigo-50"
        iconColor="text-indigo-600"
        icon={<Activity className="h-4 w-4" />}
      />
      <MetricCard
        label="Median Net Profit"
        value={formatCurrencyValue(summary.net_profit, region)}
        sub={basis}
        iconBg="bg-teal-50"
        iconColor="text-teal-600"
        icon={<DollarSign className="h-4 w-4" />}
      />
      <MetricCard
        label="Median EV"
        value={formatCurrencyValue(summary.enterprise_value, region)}
        sub={evSub}
        iconBg="bg-violet-50"
        iconColor="text-violet-600"
        icon={<Globe className="h-4 w-4" />}
      />
      <MetricCard
        label="Median ROIC"
        value={formatPercent(summary.roic)}
        sub={roicSub}
        iconBg={roicAboveWacc ? 'bg-green-50' : 'bg-red-50'}
        iconColor={roicAboveWacc ? 'text-green-600' : 'text-red-500'}
        icon={<Target className="h-4 w-4" />}
      />
      <MetricCard
        label="Median Net Debt"
        value={formatCurrencyValue(summary.net_debt, region)}
        sub={unit || basis}
        iconBg="bg-neutral-100"
        iconColor="text-neutral-500"
        icon={<CreditCard className="h-4 w-4" />}
      />
    </div>
  );
}
